import { useState, useEffect, useRef } from 'react';
import { ONE_HUNDRED_PERCENT, EVENT_LISTENERS } from './AudioPlayerConstants';
import { getRangeBackgroundStyle } from './audioPlayerHelper';

const { LOADED_META_DATA, TIME_UPDATE, ENDED, BLUR, FOCUS } = EVENT_LISTENERS;

const useAudioPlayer = ({
    audioSrc,
    progresBarFilledColor,
    progresBarUnfilledColor,
    volumeFilledColor,
    volumeUnfilledColor,
    audioLookerEvent,
}) => {
    const audio = useRef();
    const [duration, setDuration] = useState();
    const [currentTime, setCurrentTime] = useState(0);
    const [currentVolume, setCurrentVolume] = useState(100);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [prevState, setPrevState] = useState(false);

    const onBlurPage = () => {
        audio.current.pause();
    };

    const onFocusPage = () => {
        if (isPlaying) {
            audio.current.play();
        }
    };

    const setStatus = event => {
        setCurrentTime(event.target.currentTime);
    };

    const onPause = () => {
        audio.current.pause();
        setIsPlaying(false);
        setPrevState(false);
    };

    const onPlay = () => {
        audioLookerEvent();
        audio.current.play();
        setIsPlaying(true);
        setPrevState(true);
    };

    const onReset = () => {
        if (currentTime === duration) {
            onPlay();
        }
        audio.current.currentTime = 0;
        setCurrentTime(0);
    };

    const setVolume = event => {
        const { value, style } = event.target;
        const currVolume = Number(value);

        setCurrentVolume(currVolume);
        audio.current.volume = currVolume / ONE_HUNDRED_PERCENT;
        style.background = getRangeBackgroundStyle(
            currVolume,
            volumeUnfilledColor,
            volumeFilledColor,
        );
    };

    const onSeek = event => {
        const { value, style } = event.target;
        if (isPlaying) {
            audio.current.pause();
            setIsPlaying(false);
        }
        const currTime = Number(value);

        audio.current.currentTime = currTime;
        setCurrentTime(currTime);
        style.background = getRangeBackgroundStyle(
            (currTime * ONE_HUNDRED_PERCENT) / duration,
            progresBarUnfilledColor,
            progresBarFilledColor,
        );
    };

    useEffect(() => {
        const setAudioData = () => {
            setDuration(Math.round(audio.current.duration));
            setCurrentTime(Math.round(audio.current.currentTime));
        };

        const audioCurrent = audio.current;

        if (audioCurrent) {
            setIsMounted(true);
            setIsPlaying(false);
            setCurrentVolume(ONE_HUNDRED_PERCENT);

            audioCurrent.addEventListener(LOADED_META_DATA, setAudioData);
            audioCurrent.addEventListener(TIME_UPDATE, setStatus);
            audioCurrent.addEventListener(ENDED, onPause);
        }

        return () => {
            setIsMounted(false);

            if (audioCurrent) {
                audioCurrent.removeEventListener(LOADED_META_DATA, setAudioData);
                audioCurrent.removeEventListener(TIME_UPDATE, setStatus);
                audioCurrent.removeEventListener(ENDED, onPause);
            }
        };
    }, [audioSrc, isMounted]);

    useEffect(() => {
        if (isMounted) {
            window.addEventListener(BLUR, onBlurPage);
            window.addEventListener(FOCUS, onFocusPage);
        }

        return () => {
            if (isMounted) {
                window.removeEventListener(BLUR, onBlurPage);
                window.removeEventListener(FOCUS, onFocusPage);
            }
        };
    });

    return [
        audio,
        isPlaying,
        currentVolume,
        currentTime,
        duration,
        prevState,
        setVolume,
        onPause,
        onPlay,
        onSeek,
        onReset,
    ];
};

export default useAudioPlayer;
